// theme-sources/js/theme/components/product-form.js
import { logger } from "../core/logger";
import { THEME_EVENTS, publish, subscribe } from "../../shared/events";
import { fetchConfig } from "../core/utils";
import { syncCartFakeItem } from "../features/cart-fake-item";

class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector("form");
    this.variantIdInput.disabled = false;

    this.cart = document.querySelector("cart-drawer");
    this.ziggyDrawer = document.querySelector("ziggy-drawer[name='cart']");

    this.submitButton = this.querySelector('[type="submit"]');
    this.submitButtonText =
      this.submitButton?.querySelector("span.btn-add__text");

    if (!this.form || !this.submitButton) {
      logger.warn("ProductForm", "missing form or submit button");
      return;
    }

    // 🔁 Met à jour le bouton quand on change Achat unique / Abonnement
    this.addEventListener("change", (event) => {
      if (!this.isConnected) return;
      if (event.target.dataset.radioType) {
        this.updateButtonText();
      }
    });

    this.variantChangeUnsubscriber = subscribe(
      THEME_EVENTS.variantChange,
      this.handleVariantChange.bind(this),
    );

    if (this.cart) {
      this.submitButton.setAttribute("aria-haspopup", "dialog");
    }

    this.hideErrors = this.dataset.hideErrors === "true";
    this.error = false;

    this.onSubmitHandler = this.onSubmitHandler.bind(this);
    this.form.addEventListener("submit", this.onSubmitHandler);

    this.updateButtonText();

    // 🔤 Force la bonne variante selon la langue
    setTimeout(() => {
      this.setCorrectLanguageVariant();
    }, 0);

    logger.info("ProductForm", "connected");
  }

  updateButtonText() {
    if (!this.submitButtonText) return;

    const quantityField = this.querySelector('input[name="quantity"]');
    const quantityButtons = this.querySelectorAll(
      'button[name="plus"], button[name="minus"]',
    );

    // 🔴 Si bouton disabled → on affiche "Bientôt disponible"
    /*if (this.submitButton?.disabled) {
      this.submitButtonText.textContent = window.variantStrings.soldOut;*/

    if (
      this.submitButton?.disabled &&
      !this.submitButton.hasAttribute("data-already-in-cart")
    ) {
      this.submitButtonText.textContent = window.variantStrings.soldOut;

      quantityField?.setAttribute("disabled", "disabled");
      quantityField?.setAttribute("aria-disabled", "true");

      quantityButtons.forEach((button) => {
        button.setAttribute("disabled", "disabled");
        button.setAttribute("aria-disabled", "true");
      });
      return;
    }

    // 🟢 Cas normal → on réactive la quantité
    quantityField?.removeAttribute("disabled");
    quantityField?.removeAttribute("aria-disabled");

    quantityButtons.forEach((button) => {
      button.removeAttribute("disabled");
      button.removeAttribute("aria-disabled");
    });

    const sellingPlanSelected = this.querySelector(
      'input[data-radio-type="selling_plan"]:checked',
    );

    const customLabel = this.submitButton?.dataset.buyButtonLabel;

    const newText = customLabel
      ? customLabel
      : sellingPlanSelected
        ? window.variantStrings.addSubscriptionToCart
        : window.variantStrings.addToCart;

    if (this.submitButtonText.textContent !== newText) {
      this.submitButtonText.textContent = newText;
    }

    /*const newText = sellingPlanSelected
      ? window.variantStrings.addSubscriptionToCart
      : window.variantStrings.addToCart;
    if (this.submitButtonText.textContent !== newText) {
      this.submitButtonText.textContent = newText;
    }*/
  }

  setCorrectLanguageVariant() {
    const variantSelects = document.querySelector("variant-selects");
    if (!variantSelects) return; // Pas de composant variants

    // Si on arrive avec ?variant=xxx → on ne touche à rien
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("variant")) return;
    const currentLocale = document.documentElement.lang;
    const targetLang = currentLocale === "fr" ? "fr" : "int";
    const langInputs = variantSelects.querySelectorAll("input[data-lang]");
    // Pas d'option langue → on sort proprement
    if (!langInputs.length) return;

    const targetInput = variantSelects.querySelector(
      `input[data-lang="${targetLang}"]`,
    );
    // Si pas de valeur correspondante → on ne fait rien
    if (!targetInput) return;
    // Si déjà correct → on ne fait rien
    if (targetInput.checked) return;
    // On force proprement
    targetInput.checked = true;
    targetInput.dispatchEvent(new Event("change", { bubbles: true }));
  }

  handleVariantChange() {
    this.updateButtonText();
  }

  onSubmitHandler(event) {
    event.preventDefault();

    if (this.submitButton.disabled) return;

    this.error = false;
    this.handleErrorMessage();

    this.submitButton.disabled = true;
    this.submitButton.setAttribute("aria-disabled", "true");
    this.querySelector(".loading__spinner")?.classList.remove("hidden");

    const config = fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    // =========================================
    // 🔵 1️⃣ Détecter mode achat
    // =========================================
    const selectedPurchaseOption = this.form.querySelector(
      "input[data-radio-type]:checked",
    );

    const isSubscription =
      selectedPurchaseOption?.dataset.radioType === "selling_plan";

    // =========================================
    // 🔵 1️⃣ OVERRIDE selling_plan AVANT FormData
    // =========================================
    const hiddenInput = this.form.querySelector('input[name="selling_plan"]');

    if (!isSubscription) {
      // 🔴 Achat unique → surtout pas de selling_plan
      if (hiddenInput) hiddenInput.value = "";
    } else {
      // 🟢 Abonnement → override avec fréquence active du mini-cart
      const activeLabelEl = document.querySelector(
        "ziggy-plan-selector .select-item.selected",
      );

      if (activeLabelEl) {
        const activeFrequency = activeLabelEl.textContent.trim();

        const plansScript = this.querySelector(
          'script[type="application/ld+json"]#productSellingPlans',
        );

        if (plansScript) {
          const plansData = JSON.parse(plansScript.textContent);

          const matchingPlan = plansData.selling_plans.find(
            (plan) => plan.name === activeFrequency,
          );

          if (matchingPlan && hiddenInput) {
            hiddenInput.value = matchingPlan.id;
          }
        }
      }
    }

    // =========================================
    // 🟢 2️⃣ MAINTENANT créer FormData
    // =========================================
    const formData = new FormData(this.form);

    // Sécurité ultime : si one-time → supprimer selling_plan
    if (!isSubscription) {
      formData.delete("selling_plan");
    }

    logger.info("ProductForm", "FINAL selling_plan sent", {
      sellingPlan: formData.get("selling_plan"),
    });

    // =========================================
    // 🟣 Cart sections
    // =========================================
    if (this.cart) {
      formData.append(
        "sections",
        this.cart.getSectionsToRender().map((section) => section.id),
      );
      formData.append("sections_url", window.location.pathname);
      this.cart.setActiveElement(document.activeElement);
    }

    config.body = formData;

    // =========================================
    // 🔴 FETCH ADD (inchangé)
    // =========================================
    fetch(`${theme.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          publish(THEME_EVENTS.cartError, {
            source: "product-form",
            productVariantId: formData.get("id"),
            errors: response.errors || response.description,
            message: response.message,
          });

          this.handleErrorMessage(response.description);
          this.error = true;
          return;
        }

        if (!this.cart) {
          window.location = window.theme.routes.cart_url;
          return;
        }

        const sections = this.cart
          .getSectionsToRender()
          .map((section) => section.id);

        return syncCartFakeItem({
          baseState: response,
          sections,
          sectionsUrl: window.location.pathname,
        }).then((finalState) => {
          const drawerSectionId = this.cart.dataset.sectionId;
          const hasRenderableSections =
            !!finalState?.sections &&
            !!drawerSectionId &&
            !!finalState.sections[drawerSectionId] &&
            !!finalState.sections["cart-icon-bubble"];

          logger.info("ProductForm", "before renderContents", {
            hasSections: !!finalState?.sections,
            sectionKeys: Object.keys(finalState?.sections || {}),
            hasRenderableSections,
          });

          publish(THEME_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: formData.get("id"),
            cartData: finalState,
          });

          if (hasRenderableSections) {
            this.cart.renderContents(finalState);
          }

          this.ziggyDrawer?.open();
        });
      })
      .catch((error) => {
        logger.error("ProductForm", "onSubmitHandler error", error);
      })
      .finally(() => {
        if (!this.error) {
          if (this.submitButton.hasAttribute("data-hide-quantity")) {
            this.submitButton.disabled = true;
            this.submitButton.setAttribute("aria-disabled", "true");
            this.submitButton.setAttribute("data-already-in-cart", "true");

            this.querySelector("[data-in-cart-label]")?.classList.remove(
              "hidden",
            );

            const inCartLabelText = this.querySelector(
              "[data-in-cart-label-text]",
            );
            if (inCartLabelText) {
              inCartLabelText.textContent = "1 dans le panier";
            }
          } else {
            this.submitButton.disabled = false;
            this.submitButton.removeAttribute("aria-disabled");
          }
        }

        if (this.cart?.classList.contains("is-empty")) {
          this.cart.classList.remove("is-empty");
        }

        this.querySelector(".loading__spinner")?.classList.add("hidden");
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessageWrapper =
      this.errorMessageWrapper ||
      this.querySelector(".product-form__error-message-wrapper");
    if (!this.errorMessageWrapper) return;

    this.errorMessage =
      this.errorMessage ||
      this.errorMessageWrapper.querySelector(".product-form__error-message");
    this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }

  toggleSubmitButton(disable = true, text) {
    if (disable) {
      this.submitButton.setAttribute("disabled", "disabled");
      if (text) this.submitButtonText.textContent = text;
    } else {
      this.submitButton.removeAttribute("disabled");
      //this.submitButtonText.textContent = window.variantStrings.addToCart;
      this.updateButtonText();
    }
  }

  get variantIdInput() {
    return this.form.querySelector("[name=id]");
  }
}

if (!customElements.get("product-form")) {
  customElements.define("product-form", ProductForm);
}
